/* eslint-disable max-lines-per-function */
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/anlageassistent/entities/Arbeitsbereich';
import { useArbeitsbereiche } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useArbeitsbereiche/useArbeitsbereiche';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ArbeitsbereichMultiSelect({
  values,
  hideArbeitsbereiche,
  handleChange,
}: {
  values: string[];
  hideArbeitsbereiche?: string[];
  handleChange: (value: string[]) => void;
}) {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const { arbeitsbereiche } = useArbeitsbereiche(currentStandort.id);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  function onChangeFilter(value: string) {
    setFilter(value);
  }

  const options = arbeitsbereiche.filter(
    (ab) => !hideArbeitsbereiche?.includes(ab.id),
  );

  const selectedArbeitsbereiche = options.filter((ab) =>
    values.includes(ab.id),
  );
  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Button
            className="md:flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal hidden border-neutrals-300"
            variant="outline-secondary"
            role="combobox">
            <span className="truncate">
              {selectedArbeitsbereiche.length > 0 ? (
                <ArbeitsbereichItem
                  arbeitsbereich={selectedArbeitsbereiche[0]}
                />
              ) : (
                <span className="text-neutrals-700">
                  {t(
                    `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.placeholder`,
                  )}
                </span>
              )}
            </span>
            {selectedArbeitsbereiche.length > 1 && (
              <span className="text-neutrals-500 ml-auto">
                {t(
                  `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.weitere`,
                  { count: selectedArbeitsbereiche.length - 1 },
                )}
              </span>
            )}
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={`bg-white p-2 px-0 w-108 hidden md:flex`}>
          <Command>
            <CommandInput
              placeholder={t(
                `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.filterPlaceholder`,
              )}
            />
            <CommandList>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => {
                    const currentValues = selectedArbeitsbereiche;
                    if (currentValues.some((ab) => ab.id === option.id)) {
                      handleChange(
                        currentValues
                          .filter((ab) => ab.id !== option.id)
                          .map((ab) => ab.id),
                      );
                    } else {
                      handleChange([
                        ...currentValues.map((ab) => ab.id),
                        option.id,
                      ]);
                    }
                  }}>
                  <ArbeitsbereichItem arbeitsbereich={option} />
                  <Checkbox
                    className="ml-auto"
                    checked={selectedArbeitsbereiche.some(
                      (ab) => ab.id === option.id,
                    )}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Button
            className="flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal md:hidden"
            variant="outline-secondary"
            role="combobox">
            <span className="truncate">
              {selectedArbeitsbereiche.length > 0 ? (
                <ArbeitsbereichItem
                  arbeitsbereich={selectedArbeitsbereiche[0]}
                />
              ) : (
                t(
                  `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.placeholder`,
                )
              )}
            </span>
            {selectedArbeitsbereiche.length > 1 && (
              <span className="text-neutrals-500 ml-auto">
                {t(
                  `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.weitere`,
                  { count: selectedArbeitsbereiche.length - 1 },
                )}
              </span>
            )}
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t(
              `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.label`,
            )}
          </div>
          <Input
            className="grow"
            defaultValue={filter}
            placeholder={t(
              `domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.filterPlaceholder`,
            )}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2 p-2">
              {options
                .filter((o) =>
                  o.name
                    .toLocaleLowerCase()
                    .includes(filter.toLocaleLowerCase()),
                )
                .map((option) => (
                  <div
                    className="flex gap-2 not-last:border-b border-neutrals-200 pb-2"
                    key={option.id}
                    onClick={() => {
                      const currentValues = selectedArbeitsbereiche;
                      if (currentValues.some((ab) => ab.id === option.id)) {
                        handleChange(
                          currentValues
                            .filter((ab) => ab.id !== option.id)
                            .map((ab) => ab.id),
                        );
                      } else {
                        handleChange([
                          ...currentValues.map((ab) => ab.id),
                          option.id,
                        ]);
                      }
                    }}>
                    <ArbeitsbereichItem arbeitsbereich={option} />
                    <Checkbox
                      className="ml-auto"
                      checked={selectedArbeitsbereiche.some(
                        (ab) => ab.id === option.id,
                      )}
                    />
                  </div>
                ))}
            </div>
          </ScrollArea>
          <Button onClick={() => setIsDrawerOpen(false)}>
            {t('actions.fertig')}
          </Button>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function ArbeitsbereichItem({
  arbeitsbereich,
}: {
  arbeitsbereich: Arbeitsbereich;
}) {
  return (
    <div className="flex flex-row gap-2 items-center">
      <FontAwesomeIcon
        icon={faCircle}
        color={arbeitsbereich.getBorderColor()}
      />
      <span className="text-neutrals-900 text-sm font-normal">
        {arbeitsbereich.name}
      </span>
    </div>
  );
}
